import { View, Text, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useColors } from '../lib/colors';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  /**
   * If set, renders a close (X) button that navigates to this route.
   * Used for secondary pages (tax dashboard, recurring, etc.) that behave
   * like modals off a primary tab. Desktop: close shows as the header
   * action; mobile: close renders as a top-right button since the header
   * itself is hidden on narrow viewports.
   */
  closeTo?: string;
}

/**
 * Page-level header shown on desktop web only.
 * On mobile/narrow web, renders nothing — unless `closeTo` is set, in
 * which case a mobile close button is rendered on its own row.
 */
export function PageHeader({ title, subtitle, action, closeTo }: PageHeaderProps) {
  const isDesktop = useIsDesktop();
  const router = useRouter();
  const c = useColors();

  const closeButton = closeTo ? (
    <Pressable
      onPress={() => router.push(closeTo as never)}
      hitSlop={8}
      className={isDesktop ? undefined : 'p-1'}
      accessibilityRole="button"
      accessibilityLabel="Close"
    >
      <Ionicons name="close" size={24} color={c.textSecondary} />
    </Pressable>
  ) : null;

  if (!isDesktop) {
    if (!closeButton) return null;
    return (
      <View className="flex-row justify-end mb-2">
        {closeButton}
      </View>
    );
  }

  return (
    <View className="flex-row items-end justify-between mb-6">
      <View>
        <Text className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">{title}</Text>
        {subtitle && <Text className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</Text>}
      </View>
      <View className="flex-row items-center gap-2">
        {action}
        {closeButton}
      </View>
    </View>
  );
}
